import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebounce } from '@hooks/useDebounce';
import {
    getModelDefaultPageSize,
    getModelPageSizeOptions
} from '../config/modelFilters';

/**
 * Custom hook to manage Collection component state
 * Centralizes all state management logic for better maintainability
 */
export const useCollectionState = ({
    modelName,
    queryParams = {},
    modelId = null,
    default_order_by = null,
    pageSize = null,
    onRefresh = null,
    endpoint = 'getAll'
}) => {
    // Get model-specific defaults
    const defaultOrder = default_order_by;
    const defaultPageSize = pageSize || getModelDefaultPageSize(modelName);
    const pageSizeOptions = getModelPageSizeOptions(modelName);

    // Core state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [resourcesPerPage, setResourcesPerPage] = useState(defaultPageSize);
    const [order_by, setOrderBy] = useState(defaultOrder);
    const [customFilterValues, setCustomFilterValues] = useState({});

    // Debounce search term
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // Base API parameters
    const baseApiParams = useMemo(() => ({
        order_by: defaultOrder,
        ...queryParams,
        endpoint,
    }), [defaultOrder, queryParams]);

    // Get current filter parameters
    const getCurrentParams = useCallback(() => {
        const params = {
            ...baseApiParams,
            page: currentPage,
            page_size: resourcesPerPage,
            order_by: order_by,
        };

        // Add search if it has a value
        if (debouncedSearchTerm) {
            params.search = debouncedSearchTerm;
        }

        // Add custom filters if they have values
        Object.keys(customFilterValues).forEach(key => {
            if (customFilterValues[key] !== undefined && customFilterValues[key] !== '') {
                if (Array.isArray(customFilterValues[key]) && customFilterValues[key].length > 0) {
                    params[key] = customFilterValues[key];
                } else if (!Array.isArray(customFilterValues[key])) {
                    params[key] = customFilterValues[key];
                }
            }
        });

        return params;
    }, [currentPage, resourcesPerPage, order_by, debouncedSearchTerm, customFilterValues, baseApiParams]);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, customFilterValues, resourcesPerPage, order_by]);

    // Event handlers
    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleResourcesPerPageChange = useCallback((newResourcesPerPage) => {
        setResourcesPerPage(newResourcesPerPage);
    }, []);

    const handleOrderByChange = useCallback((newOrderBy) => {
        setOrderBy(newOrderBy);
    }, []);

    const handleCustomFilterChange = useCallback((filterName, value) => {
        setCustomFilterValues(prev => ({
            ...prev,
            [filterName]: value
        }));
    }, []);

    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setCustomFilterValues({});
        setCurrentPage(1);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchTerm('');
    }, []);

    // Handle refresh
    const handleRefresh = useCallback(() => {
        if (onRefresh) {
            onRefresh();
        }
        // Return current params for external refetch
        return getCurrentParams();
    }, [onRefresh, getCurrentParams]);

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return searchTerm || Object.keys(customFilterValues).some(key =>
            customFilterValues[key] !== undefined &&
            customFilterValues[key] !== '' &&
            (!Array.isArray(customFilterValues[key]) || customFilterValues[key].length > 0)
        );
    }, [searchTerm, customFilterValues]);

    return {
        // State values
        searchTerm,
        debouncedSearchTerm,
        currentPage,
        resourcesPerPage,
        order_by,
        customFilterValues,
        hasActiveFilters,

        // Configuration
        pageSizeOptions,

        // Event handlers
        handleSearchChange,
        handlePageChange,
        handleResourcesPerPageChange,
        handleOrderByChange,
        handleCustomFilterChange,
        clearFilters,
        clearSearch,
        handleRefresh,

        // Computed values
        getCurrentParams,
        baseApiParams
    };
};
